import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import client, { formatApiError, inr, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import PlacesAutocomplete from "@/components/PlacesAutocomplete";

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, totals, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const paymentMethod = "razorpay";
  const [form, setForm] = useState({
    full_name: user?.name || "",
    email: user?.email || "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    notes: "",
  });

  useEffect(() => {
    loadRazorpay();
  }, []);

  if (order) {
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-6 text-center" data-testid="order-confirmation">
        <CheckCircle2 size={60} className="mx-auto text-[#C85A32] mb-6" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">
          Order Confirmed
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl font-light">
          Thank you.
        </h1>
        <p className="mt-6 text-[#4A3B32]">
          Order <span data-testid="order-number" className="font-medium">{order.order_number}</span> is on
          the roaster's table. We'll email <b>{order.email}</b> shortly with tracking.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#1C3326]">
          <span className="w-2 h-2 bg-[#C85A32]" />
          {order.payment_status === "paid" ? "Payment received" : "Processing payment"}
        </div>
        <div className="mt-6 border border-[#EBE3D5] p-8 text-left">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#7B6F66] mb-4">Order summary</div>
          {order.items.map((it, i) => (
            <div key={i} className="flex justify-between py-2 text-sm">
              <span>{it.name} · {it.variant} × {it.quantity}</span>
              <span>{inr(it.price * it.quantity)}</span>
            </div>
          ))}
          <div className="flex justify-between mt-4 pt-4 border-t border-[#EBE3D5] font-medium">
            <span>Total</span><span>{inr(order.total)}</span>
          </div>
        </div>
        <Link
          to="/shop"
          className="inline-block mt-8 bg-[#1C3326] text-[#FDFBF7] hover:bg-[#15261C] px-10 py-4 uppercase text-xs tracking-[0.3em] transition-colors"
        >
          Continue shopping
        </Link>
        <a
          href={`${API}/orders/${order.id}/invoice.pdf`}
          target="_blank"
          rel="noreferrer"
          data-testid="confirmation-invoice"
          className="inline-block mt-8 ml-3 border border-[#1C3326] text-[#1C3326] hover:bg-[#1C3326] hover:text-[#FDFBF7] px-10 py-4 uppercase text-xs tracking-[0.3em] transition-colors"
        >
          Download Invoice
        </a>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-24 text-center max-w-2xl mx-auto px-6" data-testid="empty-checkout">
        <h1 className="font-serif-display text-5xl font-light">Your cart is empty.</h1>
        <Link
          to="/shop"
          className="inline-block mt-8 bg-[#1C3326] text-[#FDFBF7] px-10 py-4 uppercase text-xs tracking-[0.3em]"
        >
          Start shopping
        </Link>
      </div>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const payload = () => ({
    email: form.email,
    notes: form.notes,
    payment_method: paymentMethod,
    address: {
      full_name: form.full_name,
      phone: form.phone,
      line1: form.line1,
      line2: form.line2,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      country: form.country,
    },
    items: items.map((i) => ({
      product_id: i.product_id,
      slug: i.slug,
      name: i.name,
      image: i.image,
      variant: i.variant,
      price: i.price,
      quantity: i.quantity,
    })),
  });

  const openRazorpay = (created) => {
    const rzp = new window.Razorpay({
      key: created.razorpay.key_id || RAZORPAY_KEY_ID,
      amount: created.razorpay.amount,
      currency: created.razorpay.currency,
      order_id: created.razorpay.order_id,
      name: "SIGA Coffee",
      description: `Order ${created.order.order_number}`,
      image: "/logo.png",
      prefill: {
        name: form.full_name,
        email: form.email,
        contact: form.phone,
      },
      theme: { color: "#1C3326" },
      handler: async (resp) => {
        try {
          const { data: v } = await client.post("/verify-payment", {
            order_id: created.order.id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setOrder(v.order);
          clear();
          toast.success("Payment successful!");
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
        }
      },
      modal: {
        ondismiss: () => {
          setSubmitting(false);
          toast.error("Payment cancelled");
        },
      },
    });
    rzp.on("payment.failed", (resp) => {
      toast.error(resp.error?.description || "Payment failed");
      setSubmitting(false);
    });
    rzp.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await client.post("/orders", payload());
      if (data.razorpay) {
        const ok = await loadRazorpay();
        if (!ok) {
          toast.error("Failed to load payment gateway. Please try again.");
          setSubmitting(false);
          return;
        }
        openRazorpay(data);
      } else {
        toast.error("Payment gateway unavailable. Please try again.");
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Order failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-24 max-w-[1600px] mx-auto px-6 md:px-12" data-testid="checkout-page">
      <div className="mb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">Checkout</div>
        <h1 className="font-serif-display text-5xl md:text-6xl font-light leading-none">
          Almost there.
        </h1>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-6">
          <Section title="Contact">
            <Input label="Full name" required value={form.full_name} onChange={set("full_name")} testid="checkout-name" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Email" type="email" required value={form.email} onChange={set("email")} testid="checkout-email" />
              <Input label="Phone" required value={form.phone} onChange={set("phone")} testid="checkout-phone" />
            </div>
          </Section>

          <Section title="Shipping Address">
            <label className="block">
              <div className="text-xs uppercase tracking-[0.15em] text-[#7B6F66] mb-2">
                Address line 1 <span className="text-[#C85A32]">*</span>
                <span className="ml-2 text-[10px] normal-case tracking-normal text-[#7B6F66]/70">— start typing to autofill from Google</span>
              </div>
              <PlacesAutocomplete
                value={form.line1}
                onChange={(v) => setForm((f) => ({ ...f, line1: v }))}
                onPick={(p) => {
                  setForm((f) => ({
                    ...f,
                    line1: p.line1 || f.line1,
                    line2: p.line2 || f.line2,
                    city: p.city || f.city,
                    state: p.state || f.state,
                    pincode: p.pincode || f.pincode,
                    country: p.country || f.country || "India",
                  }));
                }}
                placeholder="Start typing your street address…"
                data-testid="checkout-line1"
                className="w-full bg-transparent border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none"
              />
            </label>
            <Input label="Address line 2 (optional)" value={form.line2} onChange={set("line2")} testid="checkout-line2" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input label="City" required value={form.city} onChange={set("city")} testid="checkout-city" />
              <Input label="State" required value={form.state} onChange={set("state")} testid="checkout-state" />
              <Input label="Pincode" required value={form.pincode} onChange={set("pincode")} testid="checkout-pincode" />
            </div>
          </Section>

          <Section title="Order notes (optional)">
            <textarea
              value={form.notes}
              onChange={set("notes")}
              rows={3}
              data-testid="checkout-notes"
              className="w-full bg-transparent border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none"
            />
          </Section>

          <div className="pt-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-3">Payment</div>
            <div className="border border-[#1C3326] bg-[#F4EFE6]/60 p-5" data-testid="payment-razorpay">
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-[#1C3326]">Pay online — Razorpay</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C85A32]">Secure</div>
              </div>
              <div className="text-sm text-[#7B6F66] mt-1">
                UPI · Cards · Netbanking · Wallets · EMI
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-[#EBE3D5] p-8 bg-[#F4EFE6]/50 sticky top-28">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">Your order</div>
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {items.map((it, i) => (
                <div key={i} className="flex gap-3">
                  <img src={it.image} className="w-16 h-20 object-cover" alt="" />
                  <div className="flex-1 text-sm">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-[#7B6F66]">{it.variant} × {it.quantity}</div>
                  </div>
                  <div className="text-sm">{inr(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-[#EBE3D5] space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#7B6F66]">Subtotal</span><span>{inr(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#7B6F66]">Shipping</span><span>{totals.shipping === 0 ? "Free" : inr(totals.shipping)}</span></div>
              <div className="flex justify-between font-serif-display text-2xl pt-3 border-t border-[#EBE3D5] mt-3">
                <span>Total</span><span data-testid="checkout-total">{inr(totals.total)}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="place-order-button"
              className="w-full mt-8 bg-[#C85A32] hover:bg-[#A84A28] text-[#FDFBF7] py-4 uppercase text-xs tracking-[0.3em] font-medium transition-colors disabled:opacity-60"
            >
              {submitting ? "Processing…" : `Pay ${inr(totals.total)}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[#EBE3D5] p-6 md:p-8 space-y-4">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32]">{title}</div>
      {children}
    </div>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.15em] text-[#7B6F66] mb-2">{label}</div>
      <input
        data-testid={testid}
        {...props}
        className="w-full bg-transparent border border-[#EBE3D5] px-4 py-3 focus:border-[#1C3326] focus:outline-none"
      />
    </label>
  );
}
